export interface SearchQueryFilter<T> {
  value: T;
  negated?: boolean;
}

export interface SearchQuerySort {
  key: SearchSortKey;
  order: SearchSortOrder;
}

export interface SearchQueryFilters {
  email: Array<SearchQueryFilter<string>>;
  type: Array<SearchQueryFilter<SearchDisplayContext>>;
}

export interface SearchResult {
  can_share: boolean;
  can_update_basic: boolean;
  can_view: boolean;
  allow_staff_override: boolean;
  description?: string;
  id: number;
  is_superadmin: boolean;
  last_edited: string /** i.e. "2020-05-21T20:09:28" */;
  link: string;
  name: string;
  report: string /** report type: insights, funnels, etc */;
  scores?: SearchResultScore;
  type: SearchResultType;
  unique_count: number /** number of users who viewed the entity */;
  total_count?: number;
  user: string /** name of the user */;
  user_id: number;
  email: string;
}

export interface SearchResultScore {
  time?: number;
  filter?: number;
  equality?: number;
  text?: number;
  /** the higher the number of unique views, the higher the score */
  popularity: number;
  /** score based on text match, date modified, type filter, and algolia rank */
  final: number;
}

export enum SearchResultType {
  Campaign = `ENTITY_TYPE_CAMPAIGN`,
  Cohort = `ENTITY_TYPE_COHORT`,
  Dashboard = `ENTITY_TYPE_DASHBOARD`,
  Event = `ENTITY_TYPE_EVENT`,
  Help = `ENTITY_TYPE_HELP`,
  Message = `ENTITY_TYPE_MESSAGE`,
  Report = `ENTITY_TYPE_REPORT`,
}

export enum SearchDisplayContext {
  Dashboard = `dashboard`,
  Explore = `explore`,
  Flows = `flows`,
  Funnels = `funnels`,
  Impact = `launch-analysis`,
  Insights = `insights`,
  Retention = `retention`,
}

export enum SearchDisplayContextDisplayName {
  Dashboard = `Dashboards`,
  Explore = `Explore`,
  Flows = `Flows`,
  Funnels = `Funnels`,
  Impact = `Impact`,
  Insights = `Insights`,
  Retention = `Retention`,
}

export enum SearchDisplayContextIcons {
  Dashboard = `dashboard`,
  Explore = `brand-explore`,
  Flows = `brand-flows`,
  Funnels = `brand-funnels`,
  Impact = `brand-impact`,
  Insights = `brand-insights`,
  Retention = `brand-retention`,
}

export interface RecentSearchItem {
  /** icon of the search item, could be search or brand icons */
  icon: string;
  /** unique key as RecentSearchItemType-name to be taken out*/
  key: string;
  /** label of the search item, like in items-menu */
  label: string;
  /** type of the search item */
  type: RecentSearchItemType;
  /** owner of the entity */
  user?: string;
  /** value of the search item, like in items-menu */
  value: string;

  // extra things for entities
  /** count of the entity */
  count?: number;
  /** description of the entity if applicable */
  description?: string;
  /** link to the specific entity */
  link?: string;
  /** sublabel of the search item */
  subLabel?: string;
  /** whether the current owner is the owner of the entity */
  isOwner?: boolean;
  /** report type based on its display context */
  report?: SearchDisplayContext;
}

export enum RecentSearchItemType {
  Entity = `entity`,
  SearchTerm = `searchTerm`,
}

// keep this consistent with app_api/projects/search
export enum SearchSortKey {
  LastModified = `last_edited`,
  Name = `name`,
  Popularity = `popularity`,
}

// keep this consistent with app_api/projects/search
export enum SearchSortOrder {
  Ascending = `ASC`,
  Descending = `DESC`,
}

// set sort via this enum, translates to above enums for API
export enum SearchSortOption {
  Default = `Default Sort`,
  LastModified = `Recent`,
  Popularity = `Most Viewed`,
  TitleAscending = `Title A-Z`,
  TitleDescending = `Title Z-A`,
}
